/**
 * MqlPlanTable — W55 "MQL Plan 2026"
 *
 * Full-width table: Q1/Q2/Q3/Q4 totals + monthly breakdown.
 * Year selector dropdown. Targets from marketing-targets.ts constants.
 *
 * No live query needed — targets are hardcoded constants.
 * Future: swap to /api/settings/marketing-targets for settings-driven values.
 */

import { useState } from "react";
import {
  MQL_TARGET_BY_QUARTER,
  MQL_TARGET_BY_MONTH,
  MQL_TARGET_ANNUAL,
} from "@/data/marketing-targets";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

const QUARTERS: Array<{ q: 1 | 2 | 3 | 4; months: number[] }> = [
  { q: 1, months: [1, 2, 3] },
  { q: 2, months: [4, 5, 6] },
  { q: 3, months: [7, 8, 9] },
  { q: 4, months: [10, 11, 12] },
];

const MONTH_NAMES = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function getCurrentQuarterAndMonth(): { q: number; m: number } {
  const now = new Date();
  const m = now.getMonth() + 1;
  const q = Math.ceil(m / 3);
  return { q, m };
}

export function MqlPlanTable() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const { q: currentQ, m: currentM } = getCurrentQuarterAndMonth();

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">
            MQL Plan {year}
          </div>
          <p className="text-[11px] text-neutral-400">Annual target: {MQL_TARGET_ANNUAL}</p>
        </div>
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="rounded border border-neutral-200 bg-white px-2 py-1 text-sm text-neutral-700 focus:outline-none focus:ring-1 focus:ring-[#0B0B0F]"
        >
          {YEAR_OPTIONS.map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-neutral-200">
              <th className="pb-2 text-left text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">Period</th>
              {QUARTERS.map(({ q }) => (
                <th
                  key={q}
                  className="pb-2 text-center text-[11px] font-semibold uppercase tracking-wide"
                  style={{ color: q === currentQ && year === CURRENT_YEAR ? "#DC2626" : "#6B7280" }}
                >
                  Q{q} Target
                </th>
              ))}
              <th className="pb-2 text-center text-[11px] font-semibold text-neutral-500 uppercase tracking-wide">
                Annual
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Quarter totals row */}
            <tr className="border-b border-neutral-100 bg-neutral-50">
              <td className="py-2 font-semibold text-neutral-700">Quarter total</td>
              {QUARTERS.map(({ q }) => (
                <td
                  key={q}
                  className="py-2 text-center tabular-nums font-bold"
                  style={{ color: q === currentQ && year === CURRENT_YEAR ? "#DC2626" : "#0B0B0F" }}
                >
                  {MQL_TARGET_BY_QUARTER[q]}
                </td>
              ))}
              <td className="py-2 text-center tabular-nums font-bold text-[#0F766E]">{MQL_TARGET_ANNUAL}</td>
            </tr>

            {/* Monthly rows — 3 per quarter */}
            {[0, 1, 2].map(mIdx => (
              <tr key={mIdx} className="border-b border-neutral-100 hover:bg-neutral-50">
                <td className="py-2 text-neutral-600">
                  {MONTH_NAMES[QUARTERS[0].months[mIdx]]} / {MONTH_NAMES[QUARTERS[1].months[mIdx]]} / {MONTH_NAMES[QUARTERS[2].months[mIdx]]} / {MONTH_NAMES[QUARTERS[3].months[mIdx]]}
                </td>
                {QUARTERS.map(({ q, months }) => {
                  const m = months[mIdx];
                  const isCurrent = m === currentM && year === CURRENT_YEAR;
                  return (
                    <td
                      key={q}
                      className="py-2 text-center tabular-nums text-sm"
                      style={{
                        color: isCurrent ? "#DC2626" : "#374151",
                        fontWeight: isCurrent ? 700 : 400,
                      }}
                    >
                      {MQL_TARGET_BY_MONTH[m] ?? 0}
                      {isCurrent && (
                        <span className="ml-1 text-[9px] text-[#DC2626] font-bold uppercase">now</span>
                      )}
                    </td>
                  );
                })}
                <td className="py-2 text-center tabular-nums text-[11px] text-neutral-400">
                  {/* row subtotal across quarters */}
                  {QUARTERS.reduce((s, { months }) => s + (MQL_TARGET_BY_MONTH[months[mIdx]] ?? 0), 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[10px] text-neutral-400">
        Targets are hardcoded defaults (W55). A settings-driven admin surface will replace these in a future wave.
      </p>
    </div>
  );
}
